import {useEffect, useState} from "react";
import {FiAlertTriangle, FiCheckCircle, FiLoader} from "react-icons/fi";

import {Alert} from "@/components/alert/Alert.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {NetworkRunTestResult} from "@/lib/types/network.ts";

import {NetworkProbeModal} from "./NetworkProbeModal.tsx";
import {NetworkRunTestResultList} from "./NetworkRunTestResultList.tsx";
import {RunState} from "./useNetworkRunTest.ts";

import styles from "./NetworkRunTestResults.module.scss";

/**
 * Renders the run-test result state (loading / error / summary banner + per-probe
 * list + traceroute modal). Shared by the form panel and the analysis-page panel.
 */
export const NetworkRunTestResults = ({
  state,
  result,
  error,
  title,
}: {
  state: RunState;
  result: NetworkRunTestResult | null;
  error: string | null;
  title: string;
}) => {
  const [openProbe, setOpenProbe] = useState<number | null>(null);

  useEffect(() => {
    if (state === "running") setOpenProbe(null);
  }, [state]);

  if (state === "running") {
    return (
      <div className="d-flex align-items-center gap-2 pulse-color-gray-600">
        <Icon icon={FiLoader} className="pulseSpin" /> Running test…
      </div>
    );
  }

  if (error) {
    return <p className="text-danger mb-0">{error}</p>;
  }

  if (!result) return null;

  const probes = result.probes;
  if (probes.length === 0) {
    return <p className={styles.summary}>{result.summary}</p>;
  }

  const failed = probes.filter((p) => p.status === "FAIL").length;
  // Derive pass/fail from the actual probe results so the banner icon can never
  // disagree with the "N of M locations failed" sub-line below it.
  const passed = failed === 0;
  const bannerTitle = result.summary.split("(")[0].trim();
  const bannerSub =
    failed > 0 ? `${failed} of ${probes.length} locations failed` : `All ${probes.length} locations passed`;

  return (
    <div>
      <Alert
        variant={passed ? "verification" : "error"}
        icon={passed ? FiCheckCircle : FiAlertTriangle}
        callout={bannerTitle}
        typography="bodySmall"
      >
        {bannerSub}
      </Alert>
      <div className={styles.locationsLabel}>Locations</div>
      <NetworkRunTestResultList probes={probes} onOpenProbe={setOpenProbe} />

      {openProbe !== null && (
        <NetworkProbeModal
          show
          title={title}
          probes={probes}
          context="run-test"
          initialIndex={openProbe}
          onClose={() => setOpenProbe(null)}
        />
      )}
    </div>
  );
};
